import { z } from 'zod';
import { VOLL_WALLET_MAX_LIST, VollWalletMoneyInputSchema } from './voll_wallet_money.js';

/** Um item de cadastro do legado (categoria, motivo, empresa…) com o nome para a tela. */
export const VollWalletRefSchema = z
  .object({ id: z.string().min(1), name: z.string().min(1) })
  .strict();

/** Um campo que a empresa pede no pagamento da carteira (`additionalFields`), sempre obrigatório. */
export const VollWalletAdditionalFieldSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    type: z.enum(['TEXT', 'NUMBER', 'DATE']),
  })
  .strict();
export type VollWalletAdditionalField = z.infer<typeof VollWalletAdditionalFieldSchema>;

/** O valor de um desses campos, como o viajante preencheu. */
export const VollWalletAdditionalFieldValueSchema = z
  .object({ id: z.string().min(1), value: z.string().trim().min(1) })
  .strict();

/**
 * `GET /voll-wallet/pix/form?walletId=` — o que o Pix de uma carteira pede: as categorias da
 * carteira, a alocação (a da carteira quando ela define, senão a do cadastro; `null` = nenhuma),
 * o motivo da carteira (`null` = o viajante escolhe entre `motives`) e os campos da empresa.
 */
export const VollWalletPixFormSchema = z
  .object({
    categories: z.array(VollWalletRefSchema),
    company: VollWalletRefSchema.nullable(),
    costCenter: VollWalletRefSchema.nullable(),
    motive: VollWalletRefSchema.nullable(),
    motives: z.array(VollWalletRefSchema),
    additionalFields: z.array(VollWalletAdditionalFieldSchema),
  })
  .strict();
export type VollWalletPixForm = z.infer<typeof VollWalletPixFormSchema>;

/** `POST /voll-wallet/pix/preview` — o código copia-e-cola e a cobrança. O BFF confere carteira,
 *  categoria e motivo e pede a prévia ao wallets-api; nada é debitado aqui. */
export const VollWalletPixPreviewRequestSchema = z
  .object({
    walletId: z.string().min(1),
    /** A chave Pix (CPF/CNPJ em dígitos, celular em `+55…`, e-mail, chave aleatória) ou o
     *  código copia-e-cola (o texto do QR), como o app legado manda no `paymentCode`. */
    paymentCode: z.string().trim().min(5).max(1000),
    categoryId: z.string().min(1),
    /** Com o nome: a lista de motivos é paginada, e o BFF confere buscando por ele. */
    motive: VollWalletRefSchema,
    observation: z.string().trim().max(500).optional(),
    additionalFields: z.array(VollWalletAdditionalFieldValueSchema).max(VOLL_WALLET_MAX_LIST),
  })
  .strict();
export type VollWalletPixPreviewRequest = z.infer<typeof VollWalletPixPreviewRequestSchema>;

/**
 * A prévia para a tela de confirmação. `previewToken` é opaco e assinado pelo BFF: amarra o
 * pagamento a ESTA prévia, deste viajante, por alguns minutos. `amount` é o valor do código;
 * `null` = o código não traz valor e o viajante digita.
 */
export const VollWalletPixPreviewSchema = z
  .object({
    previewToken: z.string().min(1),
    /** O pagamento que a prévia abriu, o mesmo id que o pagamento devolve: com ele a tela pergunta
     *  o desfecho do Pix mesmo quando o pagamento não respondeu. */
    paymentId: z.string().min(1),
    payee: z
      .object({
        name: z.string().nullable(),
        taxId: z.string().nullable(),
        bank: z.string().nullable(),
      })
      .strict(),
    amount: z.number().positive().nullable(),
  })
  .strict();
export type VollWalletPixPreview = z.infer<typeof VollWalletPixPreviewSchema>;

/** `POST /voll-wallet/pix/payments` — paga a prévia. O valor só vale quando o código não traz um. */
export const VollWalletPixPayRequestSchema = z
  .object({ previewToken: z.string().min(1), amount: VollWalletMoneyInputSchema })
  .strict();
export type VollWalletPixPayRequest = z.infer<typeof VollWalletPixPayRequestSchema>;

export const VollWalletPixPaymentSchema = z
  .object({ paymentId: z.string().min(1), invoiceRequired: z.boolean() })
  .strict();
export type VollWalletPixPayment = z.infer<typeof VollWalletPixPaymentSchema>;

/** `GET /voll-wallet/pix/payments/:id` — o desfecho. `pending` até o banco responder; `unknown`
 *  quando o BFF não tem como saber (sem a fonte do desfecho ou a leitura falhou): a tela para de
 *  perguntar e manda conferir no extrato. Também para de perguntar depois de um tempo. */
export const VollWalletPixStatusSchema = z
  .object({ status: z.enum(['paid', 'failed', 'pending', 'unknown']) })
  .strict();
export type VollWalletPixStatus = z.infer<typeof VollWalletPixStatusSchema>;

/** As recusas que a tela do Pix explica. `invalid_payment_code`: o wallets-api não aceitou o
 *  código; `preview_expired`: a prévia passou do prazo e precisa ser refeita;
 *  `payment_rejected`: o pagamento foi recusado (fraude, banco). */
export const VollWalletPixErrorSchema = z.discriminatedUnion('error', [
  z.object({ error: z.literal('wallet_unavailable') }).strict(),
  z.object({ error: z.literal('category_unavailable') }).strict(),
  z.object({ error: z.literal('motive_unavailable') }).strict(),
  z.object({ error: z.literal('allocation_missing') }).strict(),
  z.object({ error: z.literal('invalid_payment_code') }).strict(),
  /** A chave não tem conta no DICT: o viajante corrige a chave e refaz a prévia. */
  z.object({ error: z.literal('pix_key_not_found') }).strict(),
  z.object({ error: z.literal('preview_expired') }).strict(),
  z.object({ error: z.literal('amount_mismatch') }).strict(),
  /** O saldo da carteira não cobre o valor no momento do pagamento; nada foi debitado. */
  z.object({ error: z.literal('insufficient_balance') }).strict(),
  z.object({ error: z.literal('payment_rejected') }).strict(),
  /** O wallets-api falhou sem recusar: não dá para afirmar que nada foi feito (#5688). */
  z.object({ error: z.literal('outcome_unknown') }).strict(),
  /** O agente de auditoria tem prestação de contas aberta com o viajante. */
  z.object({ error: z.literal('audit_blocked') }).strict(),
  /** O termo da política da wallet ainda não foi aceito. */
  z.object({ error: z.literal('policy_pending') }).strict(),
]);
export type VollWalletPixError = z.infer<typeof VollWalletPixErrorSchema>;
